import { useNotification } from "@/shared/feedback/notification";
import { useMutation } from "@apollo/client";
import { EntityBasedDynamicProcessInput } from "gql/graphql";
import { SAVE_DYNAMIC_PROCESS } from "../api/save-dynamic-process";
import { GET_ALL_DYNAMIC_PROCESSES } from "../../../entities/dynamic-processes-list/api/get-all-dynamic-processes";
import { FormProviderProps } from "antd/lib/form/context";
import { useTranslations } from "use-intl";
import { toProcessRouteStepInput } from "@/shared/form/process-route-step-input";
import { routeHasIncompleteSteps } from "@/shared/form/process-route-step-input";

export const ROUTE = "Route";
export const INIT = "Initiator";
export const APPROVER = "Approver";
export const INFO = "Info";

export function useForm({ afterSubmit }: { afterSubmit?: () => void }) {
  const t = useTranslations("BpmProcessForm");
  const tc = useTranslations("Common");
  const notify = useNotification();

  const [save, { loading }] = useMutation(SAVE_DYNAMIC_PROCESS, {
    refetchQueries: [GET_ALL_DYNAMIC_PROCESSES],
  });

  const onFormFinish: FormProviderProps["onFormFinish"] = async (_, info) => {
    try {
      await Promise.all(
        Object.keys(info.forms).map((formName) =>
          info.forms[formName].validateFields(),
        ),
      );
    } catch (e) {
      notify?.error({
        message: "Process is not valid",
      });
      return;
    }

    let process = {} as EntityBasedDynamicProcessInput;

    Object.keys(info.forms).forEach((formName) => {
      const formValue = info.forms[formName].getFieldsValue();

      switch (formName) {
        case INFO:
          process = {
            ...process,
            ...formValue,
          };
          break;
        case ROUTE:
          process = {
            ...process,
            ...formValue,
          };
          break;
        case INIT:
          process = {
            ...process,
          };
          process["initiatorNotificationTemplate"] = formValue;
          break;
        case APPROVER:
          process = {
            ...process,
          };
          process["approverNotificationTemplate"] = formValue;
          break;
        default:
          break;
      }
    });

    // Шаг без персоны и без роли — это шаг со скрытым участником: тип шага не
    // восстановился, обязательные поля не смонтировались, валидация его пропустила.
    // Сохранение стёрло бы участника из маршрута.
    if (routeHasIncompleteSteps(process.route ?? [])) {
      notify?.error({
        message: tc("HIDDEN_FIELDS_BLOCK_SAVE"),
      });
      return;
    }

    const response = await save({
      variables: {
        process: {
          ...process,
          route: (process.route ?? []).map(toProcessRouteStepInput),
        },
      },
    });

    if (response.data?.saveEntityBasedDynamicProcessModel) {
      notify?.success({
        message: t("Process saved"),
      });

      afterSubmit?.();
    }
  };

  return {
    formProps: {
      onFormFinish,
    },
    loading,
  };
}
